"use client";

import React from "react";
import Link from "next/link";
import { navClientItems, navToolItems, settingsPath } from "@/lib/config/nav";
import Image from "next/image";
import { usePathname } from "next/navigation";
import { cn, getFullNameFromFirstLast, getInitials } from "@/lib/utils";
import { Label } from "../ui/label";
import { useAtom, useAtomValue } from "jotai";
import { selectedClientAtom } from "@/app/store/global";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { ClientDisplay } from "@/interfaces/client";
import { useRouter } from "next/navigation";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { Button } from "../ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "../ui/avatar";
import { LogOut, Settings } from "lucide-react";
import { logout } from "@/server/actions/auth";

interface SideNavProps {
  clientOptions: ClientDisplay[];
  profilePictureUrl: string;
  firstName: string;
  lastName: string;
  primaryEmail: string;
}

export function SideNav(props: SideNavProps) {
  const {
    clientOptions,
    profilePictureUrl,
    firstName,
    lastName,
    primaryEmail,
  } = props;
  const path = usePathname();
  const router = useRouter();
  const [selectedClient, setSelectedClient] = useAtom(selectedClientAtom);

  const handleClientChange = (client: string) => {
    setSelectedClient(client);
    // get current child path of client
    const currentPathOfClient = path.split("/").slice(2).join("/");
    if (currentPathOfClient) {
      router.push(`/${client}/${currentPathOfClient}`);
      return;
    }
  };

  return (
    <nav className="flex flex-col h-full items-start justify-between sm:py-5">
      <div className="flex flex-col gap-2 w-full pl-4 pr-4">
        <Link
          href="/"
          className="group flex h-8 w-8 shrink-0 items-center justify-start gap-2 mb-2 ml-2 rounded-full text-lg font-semibold text-primary-foreground md:h-8 md:text-base"
        >
          <Image
            alt="logo"
            src="/images/logo.svg"
            height={0}
            width={0}
            style={{ width: "32px", height: "auto" }}
          />
          <span className="text-black font-semibold w-12">Cape</span>
        </Link>
        <div className="mt-1 px-2 flex flex-col gap-2 w-full">
          <Select
            onValueChange={handleClientChange}
            value={selectedClient || undefined}
          >
            <SelectTrigger className="bg-white">
              <SelectValue placeholder="Select a client" />
            </SelectTrigger>
            <SelectContent className="text-black">
              {clientOptions.map((client) => (
                <SelectItem key={client.name} value={client.name}>
                  {client.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="ml-2 mt-8 pr-2 flex flex-col gap-1 w-full">
          <Label className="mb-2 text-sm text-muted-foreground">Manage</Label>
          {navClientItems.map((item) => (
            <Link
              key={item.name}
              href={
                item.disableParentPath
                  ? `/${item.href}`
                  : `/${selectedClient}/${item.href}`
              }
              className={cn(
                "flex flex-row w-full p-2 space-x-2 items-center justify-start rounded-md transition-colors hover:bg-cape-muted-background",
                path.includes(item.href) ? "bg-zinc-100" : "",
              )}
            >
              <item.icon className="h-4 w-4" />
              <span className="text-sm">{item.name}</span>
            </Link>
          ))}
        </div>
        <div className="ml-2 mt-8 pr-2 flex flex-col gap-1 w-full">
          <Label className="mb-2 text-sm text-muted-foreground">Tools</Label>
          {navToolItems.map((item) => (
            <Link
              key={item.name}
              href={item.href}
              className={cn(
                "flex flex-row w-full py-2 space-x-2 px-2 items-center justify-start rounded-md transition-colors hover:bg-cape-muted-background",
                path.includes(item.href) ? "bg-zinc-100" : "",
              )}
            >
              <item.icon className="h-4 w-4" />
              <span className="text-sm">{item.name}</span>
            </Link>
          ))}
        </div>
      </div>
      <div className="flex flex-row items-center justify-center gap-2 w-full pt-4 px-1 border-t">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="outline"
              className="relative h-7 w-full space-x-2 px-0 border-0 bg-cape-background justify-start pl-2"
            >
              <Avatar className="h-8 w-8">
                <AvatarImage src={profilePictureUrl} />
                <AvatarFallback className="bg-cape-muted-background text-black font-semibold border-0 text-md">
                  {getInitials(getFullNameFromFirstLast(firstName, lastName))}
                </AvatarFallback>
              </Avatar>
              <span className="text-black text-base border-0">
                {getFullNameFromFirstLast(firstName, lastName)}
              </span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="w-56" align="end">
            <DropdownMenuLabel className="font-normal">
              <div className="flex flex-col space-y-1">
                <p className="text-sm font-medium leading-none">CapeX</p>
                <p className="text-xs leading-none text-muted-foreground">
                  {primaryEmail}
                </p>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuGroup>
              <DropdownMenuItem onClick={() => router.push(settingsPath)}>
                <Settings className="mr-2 h-4 w-4" />
                <span>Settings</span>
              </DropdownMenuItem>
            </DropdownMenuGroup>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => logout()}>
              <LogOut className="mr-2 h-4 w-4" />
              <span>Log out</span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </nav>
  );
}
